<script setup lang="ts">
/**
 * 產品頁〈代管設定〉頁籤（批141，主人裁示 14：結構化表單＋JSON 檢視與貼上）。
 *
 * 管的是 `products.options.hosting_profile`——「這個產品的站台在我方主機上要怎麼開」。
 * 缺這份設定或設定無效＝這個產品**開不了站**（後端 fail-closed），即使勾了
 * 「開放由我方代管」也一樣，所以兩者不一致時要用紅字講出來。
 *
 * - 讀＝admin 以上（產品頁本身的門檻）；**寫＝營運主管以上**（`canEdit`，後端 `require_manager`）。
 * - 存檔走專屬端點 `PUT …/hosting-profile`（嚴格驗證、422 帶 `field`），**不經**
 *   〈憑證設計〉的整包存檔——那一支會忽略 body 裡的這個鍵。
 * - 前端預檢（`lib/hostingProfile.ts::validateForm`）只是讓紅字提早出現；權威在後端。
 * - 文案一律中性：本元件不寫任何產品名，placeholder 用「<產品>」代稱。
 * - 批151 C6：選填的〈站台機密欄位〉（`site_secrets`）——只宣告欄位鍵與環境變數名，
 *   **值不在這裡**（站台詳情頁由營運主管設定）。沒有任何列又本來沒有這個鍵＝存檔不送。
 */
import { computed, nextTick, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { ProductSpec } from '@/api/ls'
import {
  CHANNEL_HINT,
  CHANNEL_LABEL,
  FEATURE_KEYS,
  FEATURE_LABELS,
  LOGIN_METHOD_LABELS,
  LOGIN_METHODS,
  MEM_LIMITS_MAX_KEYS,
  PATH_KEYS,
  PATH_LABELS,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  SITE_SECRET_ENV_DENY_PREFIX,
  SITE_SECRET_HINT_MAX,
  SITE_SECRET_LABEL_MAX,
  SITE_SECRETS_MAX,
  asProfileIssue,
  emptyForm,
  formFromProfile,
  issueHits,
  needsSetupToken,
  profileFromForm,
  profileJson,
  profileState,
  rawProfileOf,
  sameProfile,
  secretSentIndex,
  validateForm,
  validateProfile,
} from '@/lib/hostingProfile'
import type { PathKey, ProfileForm, ProfileIssue } from '@/lib/hostingProfile'
import { ROLE_LABELS } from '@/lib/roles'
import { useDialogsStore } from '@/stores/dialogs'

const props = defineProps<{
  product: ProductSpec
  /** 全部產品（「從其他產品帶入」用）。 */
  products: ProductSpec[]
  /** 營運主管以上才可編輯（其餘唯讀）。 */
  canEdit: boolean
}>()
const emit = defineEmits<{ saved: [] }>()

const dialogs = useDialogsStore()

/** 目錄樣板的中性舉例（`<產品>` 是代稱，不是字面）。 */
const PATH_PLACEHOLDERS: Record<PathKey, string> = {
  data_dir: '例：/opt/data/<產品>/{slug}',
  install_dir: '例：/opt/data/bundles/<產品>-{slug}',
  backup_dir: '例：/opt/backup/<產品>/{slug}',
}

const storedRaw = computed(() => rawProfileOf(props.product.options))
const state = computed(() => profileState(props.product.options))
/** DB 裡現存那份為什麼無效（`state === 'invalid'` 才有）。 */
const storedIssue = computed(() => (state.value === 'invalid' ? validateProfile(storedRaw.value) : null))
const hostingOn = computed(() => props.product.options?.supports_hosting === true)
const STATE_PILL: Record<string, { cls: string; text: string }> = {
  ok: { cls: 'active', text: '已設定' },
  none: { cls: 'superseded', text: '未設定' },
  invalid: { cls: 'revoked', text: '格式無效' },
}

const form = ref<ProfileForm>(emptyForm())
/** 後端 422 回來的錯（改了表單就清掉——那一格可能已經被改對了）。 */
const serverIssue = ref<ProfileIssue | null>(null)
/** 按過一次儲存才把前端預檢的紅字標到欄位上（空表單一打開就滿版紅字只是噪音）。 */
const showErrors = ref(false)
const saving = ref(false)
const deleting = ref(false)

function reset() {
  const raw = storedRaw.value
  form.value = raw === undefined || raw === null ? emptyForm() : formFromProfile(raw)
  serverIssue.value = null
  showErrors.value = false
  jsonMode.value = 'view'
  jsonError.value = null
}

const liveIssue = computed(() => validateForm(form.value))
const issue = computed<ProfileIssue | null>(
  () => serverIssue.value ?? (showErrors.value ? liveIssue.value : null),
)
function hit(cell: string): boolean {
  return issueHits(issue.value, cell)
}
watch(
  form,
  () => {
    serverIssue.value = null
  },
  { deep: true },
)

/** 目前表單轉出去的 JSON（與存檔送出的是同一份）。 */
const formJson = computed(() => profileFromForm(form.value))
/** 與 DB 現值不同＝還沒存（壞掉的現值恆視為不同）。 */
const dirty = computed(() => {
  const raw = storedRaw.value
  if (raw === undefined || raw === null) return true
  return !sameProfile(formJson.value, raw)
})

// --- 記憶體上限列 ---------------------------------------------------------
function addMemRow() {
  if (form.value.mem.length >= MEM_LIMITS_MAX_KEYS) return
  form.value.mem.push({ key: '', value: '' })
}
function removeMemRow(i: number) {
  form.value.mem.splice(i, 1)
  if (!form.value.mem.length) form.value.mem.push({ key: '', value: '' })
}

// --- 站台機密欄位（批151 C6）-----------------------------------------------
function addSecretRow() {
  if (form.value.secrets.length >= SITE_SECRETS_MAX) return
  form.value.secrets.push({ key: '', env: '', label: '', hint: '' })
}
function removeSecretRow(i: number) {
  form.value.secrets.splice(i, 1)
}
/** 錯誤點名的是不是表單第 i 列的某一格（錯誤用的是送出陣列的索引，要先換算）。 */
function secretHit(i: number, cell: 'key' | 'env' | 'label' | 'hint'): boolean {
  const n = secretSentIndex(form.value.secrets, i)
  return n >= 0 && hit(`site_secrets[${n}].${cell}`)
}
function secretRowHit(i: number): boolean {
  const n = secretSentIndex(form.value.secrets, i)
  return n >= 0 && hit(`site_secrets[${n}]`)
}

// --- 從其他產品帶入 -------------------------------------------------------
const copySources = computed(() =>
  props.products.filter((p) => p.key !== props.product.key && profileState(p.options) === 'ok'),
)
const copyFrom = ref('')
function applyCopy() {
  const src = copySources.value.find((p) => p.key === copyFrom.value)
  copyFrom.value = ''
  if (!src) return
  form.value = formFromProfile(rawProfileOf(src.options))
  showErrors.value = false
}

// --- JSON 檢視／貼上 --------------------------------------------------------
const jsonOpen = ref(false)
const jsonMode = ref<'view' | 'paste'>('view')
const jsonDraft = ref('')
const jsonError = ref<string | null>(null)
const copied = ref(false)

// ★ 放在所有 ref 宣告之後：`immediate` 會當場呼叫 `reset()`，它用到上面的 JSON 狀態。
//   換產品（或存檔後父層重載、產品物件被換掉）一律重設回伺服器上的內容。
watch(() => props.product, reset, { immediate: true })

function openPaste() {
  jsonDraft.value = profileJson(formJson.value)
  jsonError.value = null
  jsonMode.value = 'paste'
}
function cancelPaste() {
  jsonMode.value = 'view'
  jsonError.value = null
}
function applyPaste() {
  let parsed: unknown
  try {
    parsed = JSON.parse(jsonDraft.value)
  } catch {
    jsonError.value = '不是合法的 JSON，請確認整段都完整無缺。'
    return
  }
  // ★ 驗不過就**不套用**：表單讀不進未知鍵（會被靜默丟掉），先講清楚哪裡不合再讓人改。
  const bad = validateProfile(parsed)
  if (bad) {
    // 裁示 48：授權中心 1.0.61 以前的兩格 `channels`——講清楚要改成什麼，而不只是「不認得的鍵」。
    jsonError.value =
      bad.code === 'unknown_key' && bad.field === 'channels'
        ? '這是舊格式（channels 分測試／正式兩格）。現在只有一格 "channel"：填這台授權中心開站要下發的 channel，並拿掉 channels。'
        : `${bad.field ? `欄位 ${bad.field}：` : ''}${bad.message}`
    return
  }
  form.value = formFromProfile(parsed)
  jsonMode.value = 'view'
  jsonError.value = null
}
async function copyJson() {
  try {
    await navigator.clipboard.writeText(profileJson(formJson.value))
    copied.value = true
    window.setTimeout(() => (copied.value = false), 2000)
  } catch {
    await dialogs.notify({
      title: '無法自動複製',
      message: '瀏覽器不允許自動複製（需 HTTPS 或已授權剪貼簿），請手動選取文字複製。',
    })
  }
}

// --- 儲存／移除 -----------------------------------------------------------
function errorDetail(e: unknown): unknown {
  return e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
}
function errorMessage(e: unknown, fallback: string): string {
  const d = errorDetail(e)
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  return fallback
}

/** 錯誤說明列（存檔被擋時捲到它——儲存鈕在頁尾，紅字在頁首，不捲的話看起來像按了沒反應）。 */
const issueEl = ref<HTMLElement | null>(null)
async function revealIssue() {
  await nextTick()
  issueEl.value?.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

async function save() {
  showErrors.value = true
  if (liveIssue.value) {
    await revealIssue()
    return
  }
  saving.value = true
  try {
    await ls.putHostingProfile(props.product.id, formJson.value)
    emit('saved')
    await dialogs.notify({
      title: '已儲存代管設定',
      message: '之後的開站與派工會照這份設定；既有站台的目錄與容器不會被搬動。',
    })
  } catch (e) {
    const found = e instanceof ApiError && e.status === 422 ? asProfileIssue(errorDetail(e)) : null
    if (found) {
      serverIssue.value = found
      await revealIssue()
      return
    }
    await dialogs.notify({ title: '儲存失敗', message: errorMessage(e, '儲存代管設定失敗') })
  } finally {
    saving.value = false
  }
}

async function remove() {
  const ok = await dialogs.confirm({
    title: '移除代管設定',
    message:
      `移除「${props.product.name}」的代管設定？\n` +
      '移除之後這個產品開不了新站；既有站台照常運作，但依賴這份設定的功能（公開網址、' +
      '主機設定下發等）會一律視為不支援。',
    danger: true,
    confirmText: '移除',
  })
  if (!ok) return
  deleting.value = true
  try {
    await ls.deleteHostingProfile(props.product.id)
    emit('saved')
  } catch (e) {
    await dialogs.notify({ title: '移除失敗', message: errorMessage(e, '移除代管設定失敗') })
  } finally {
    deleting.value = false
  }
}
</script>

<template>
  <div class="hp">
    <div class="hp-head">
      <span class="status-pill" :class="STATE_PILL[state].cls">{{ STATE_PILL[state].text }}</span>
      <span class="subtle">
        開放由我方代管：<strong>{{ hostingOn ? '是' : '否' }}</strong>（在〈憑證設計〉頁切換）
      </span>
    </div>

    <p v-if="hostingOn && state !== 'ok'" class="hp-alert">
      ⚠ 這個產品已開放由我方代管，但{{ state === 'none' ? '還沒有代管設定' : '代管設定格式無效' }}
      ——目前<strong>開不了站</strong>。請在下方補齊並儲存。
    </p>
    <p v-if="storedIssue" class="hp-alert">
      現存的代管設定不合格式：{{ storedIssue.field ? `欄位 ${storedIssue.field}：` : '' }}{{ storedIssue.message }}
    </p>
    <p v-if="state === 'none'" class="subtle">
      還沒有代管設定。開放代管之前，先在這裡把出貨 channel、目錄、容器記憶體與功能旗標設好。
    </p>

    <div v-if="canEdit && copySources.length" class="hp-copy">
      <select v-model="copyFrom" aria-label="從其他產品帶入">
        <option value="" disabled>從其他產品帶入…</option>
        <option v-for="p in copySources" :key="p.key" :value="p.key">{{ p.name }}（{{ p.key }}）</option>
      </select>
      <UiButton variant="secondary" :disabled="!copyFrom" @click="applyCopy">帶入</UiButton>
      <small class="subtle">帶入後請改成這個產品在這台授權中心的 channel 與目錄樣板。</small>
    </div>

    <p v-if="issue" ref="issueEl" class="hp-issue" role="alert">
      {{ issue.field ? `欄位 ${issue.field}：` : '' }}{{ issue.message }}
    </p>

    <!-- 出貨 channel -->
    <div class="card-head sub"><h3 class="section-title">出貨 channel</h3></div>
    <div class="hp-grid half">
      <label class="field">
        <span class="field-label">{{ CHANNEL_LABEL }}</span>
        <input
          v-model="form.channel"
          class="mono"
          :class="{ invalid: hit('channel') }"
          :disabled="!canEdit"
          placeholder="例：<產品>-dev 或 <產品>-prod"
          maxlength="32"
        />
      </label>
    </div>
    <p class="subtle">{{ CHANNEL_HINT }}從別台授權中心帶入或匯入時，記得改成這台的值。</p>

    <!-- DNS -->
    <div class="card-head sub"><h3 class="section-title">網域</h3></div>
    <div class="hp-grid">
      <label class="field">
        <span class="field-label">代管網域（zone）</span>
        <input
          v-model="form.dnsZone"
          class="mono"
          :class="{ invalid: hit('dns.zone') }"
          :disabled="!canEdit"
          placeholder="留空＝沿用系統的代管網域"
        />
        <small class="subtle">站台網址＝<code class="mono">&lt;代號&gt;.&lt;zone&gt;</code>。</small>
      </label>
      <label class="field">
        <span class="field-label">Cloud DNS zone 名稱</span>
        <input
          v-model="form.dnsManagedZone"
          class="mono"
          :class="{ invalid: hit('dns.clouddns_managed_zone') }"
          :disabled="!canEdit"
          placeholder="留空＝沿用系統設定"
        />
        <small class="subtle">自訂了網域卻沒填這一格＝該網域的記錄改由人工建立。</small>
      </label>
    </div>

    <!-- 目錄 -->
    <div class="card-head sub"><h3 class="section-title">站台目錄樣板</h3></div>
    <div class="hp-grid three">
      <label v-for="k in PATH_KEYS" :key="k" class="field">
        <span class="field-label">{{ PATH_LABELS[k] }}</span>
        <input
          v-model="form.paths[k]"
          class="mono"
          :class="{ invalid: hit(`paths.${k}`) }"
          :disabled="!canEdit"
          :placeholder="PATH_PLACEHOLDERS[k]"
        />
      </label>
    </div>
    <p class="subtle">
      必須在 <code class="mono">/opt/data</code> 或 <code class="mono">/opt/backup</code> 底下、恰好含一次
      <code class="mono">{slug}</code> 且在最後一段；三個目錄兩兩不同。
    </p>

    <!-- 容器記憶體 -->
    <div class="card-head sub">
      <h3 class="section-title">容器記憶體上限</h3>
      <span class="spacer" />
      <UiButton
        v-if="canEdit"
        size="sm"
        variant="secondary"
        :disabled="form.mem.length >= MEM_LIMITS_MAX_KEYS"
        @click="addMemRow"
      >
        ＋ 新增一列
      </UiButton>
    </div>
    <div class="mem-rows" :class="{ invalid: hit('mem_limits') }">
      <div v-for="(row, i) in form.mem" :key="i" class="mem-row">
        <input
          v-model="row.key"
          class="mono mem-key"
          :class="{ invalid: hit(`mem_limits.${row.key.trim()}`) }"
          :disabled="!canEdit"
          placeholder="容器鍵（例：api）"
          maxlength="24"
        />
        <input
          v-model="row.value"
          class="mono mem-val"
          :class="{ invalid: hit(`mem_limits.${row.key.trim()}`) }"
          :disabled="!canEdit"
          placeholder="例：1g、384m"
          maxlength="8"
        />
        <button v-if="canEdit" type="button" class="x danger" title="移除這一列" @click="removeMemRow(i)">✕</button>
      </div>
    </div>
    <p class="subtle">1–{{ MEM_LIMITS_MAX_KEYS }} 列；容器鍵是站台 compose 裡的服務代稱，值是「正整數＋k/m/g」。</p>

    <!-- 資源門檻 -->
    <div class="card-head sub"><h3 class="section-title">開站資源門檻</h3></div>
    <div class="hp-grid three">
      <label v-for="k in RESOURCE_KEYS" :key="k" class="field">
        <span class="field-label">{{ RESOURCE_LABELS[k] }}</span>
        <input
          v-model="form.resources[k]"
          inputmode="numeric"
          :class="{ invalid: hit(`resources.${k}`) }"
          :disabled="!canEdit"
          placeholder="留空＝沿用系統"
        />
      </label>
    </div>

    <!-- 功能 -->
    <div class="card-head sub"><h3 class="section-title">功能</h3></div>
    <div class="feat-list">
      <label v-for="k in FEATURE_KEYS" :key="k" class="chk" :class="{ invalid: hit(`features.${k}`) }">
        <input v-model="form.features[k]" type="checkbox" :disabled="!canEdit" />
        <span>
          {{ FEATURE_LABELS[k].label }}
          <small class="subtle">{{ FEATURE_LABELS[k].hint }}</small>
        </span>
      </label>
    </div>
    <p class="subtle">關掉的功能：開站不送對應參數、後台隱藏相關欄位與按鈕、相關端點一律拒絕。</p>

    <!-- 首位管理員 -->
    <div class="card-head sub"><h3 class="section-title">首位管理員</h3></div>
    <div class="field">
      <span class="field-label">允許的登入方式</span>
      <div class="field-row" :class="{ 'invalid-box': hit('login_methods') }">
        <label v-for="m in LOGIN_METHODS" :key="m" class="chk">
          <input v-model="form.login[m]" type="checkbox" :disabled="!canEdit" />
          {{ LOGIN_METHOD_LABELS[m] }}
        </label>
      </div>
      <small class="subtle">
        不含 Google＝客戶申請代管時密碼必填；允許 Google 時「代管 Google 登入」與「反向隧道」兩個功能都要勾。
      </small>
    </div>
    <label class="field setup-field">
      <span class="field-label">初始設定網址路徑</span>
      <input
        v-model="form.setupPath"
        class="mono"
        :class="{ invalid: hit('setup_path') }"
        :disabled="!canEdit"
        placeholder="例：/setup 或 /setup?token={setup_token}"
        maxlength="200"
      />
      <small class="subtle">
        交付網址＝<code class="mono">https://&lt;站台網域&gt;&lt;路徑&gt;</code>。
        <template v-if="needsSetupToken(form.setupPath)">
          含 <code class="mono">{setup_token}</code>：開站時會產生一次性權杖代入，網址只在開站結果顯示這一次。
        </template>
        <template v-else>要一次性權杖就加上 <code class="mono">{setup_token}</code>（最多一次）。</template>
      </small>
    </label>

    <!-- 站台機密欄位（批151 C6，選填） -->
    <div class="card-head sub">
      <h3 class="section-title">站台機密欄位</h3>
      <span class="spacer" />
      <UiButton
        v-if="canEdit"
        size="sm"
        variant="secondary"
        :disabled="form.secrets.length >= SITE_SECRETS_MAX"
        @click="addSecretRow"
      >
        ＋ 新增欄位
      </UiButton>
    </div>
    <p class="subtle">
      選填。這個產品的代管站需要我方替它設定哪些機密（例如站台自己要用的第三方服務金鑰）——這裡只宣告
      <strong>欄位與寫進站台的環境變數名</strong>，值不在這裡：由{{ ROLE_LABELS.manager }}在站台詳情頁設定、更換或清除。
    </p>
    <div v-if="form.secrets.length" class="secret-rows" :class="{ invalid: hit('site_secrets') }">
      <div class="secret-row secret-head" aria-hidden="true">
        <span>欄位鍵</span><span>環境變數名</span><span>名稱</span><span>說明</span><span />
      </div>
      <div v-for="(row, i) in form.secrets" :key="i" class="secret-row" :class="{ 'invalid-box': secretRowHit(i) }">
        <input
          v-model="row.key"
          class="mono"
          :class="{ invalid: secretHit(i, 'key') }"
          :disabled="!canEdit"
          placeholder="例：api_key"
          maxlength="32"
          aria-label="欄位鍵"
        />
        <input
          v-model="row.env"
          class="mono"
          :class="{ invalid: secretHit(i, 'env') }"
          :disabled="!canEdit"
          placeholder="例：<產品>_API_KEY"
          maxlength="64"
          aria-label="環境變數名"
        />
        <input
          v-model="row.label"
          :class="{ invalid: secretHit(i, 'label') }"
          :disabled="!canEdit"
          placeholder="例：第三方服務金鑰"
          :maxlength="SITE_SECRET_LABEL_MAX"
          aria-label="名稱"
        />
        <input
          v-model="row.hint"
          :class="{ invalid: secretHit(i, 'hint') }"
          :disabled="!canEdit"
          placeholder="選填"
          :maxlength="SITE_SECRET_HINT_MAX"
          aria-label="說明"
        />
        <button v-if="canEdit" type="button" class="x danger" title="移除這一列" @click="removeSecretRow(i)">✕</button>
        <span v-else />
      </div>
    </div>
    <p v-else class="subtle">這個產品沒有站台機密欄位。</p>
    <p class="subtle">
      最多 {{ SITE_SECRETS_MAX }} 項。欄位鍵：小寫英數與底線、字母開頭、最長 32 字；環境變數名：大寫英數與底線、2–64 字，
      不可以 <code class="mono">{{ SITE_SECRET_ENV_DENY_PREFIX }}</code> 開頭，也不可與主機設定樣板的變數撞名（開站與設定時檢查）。
      改了環境變數名之後，站台上已寫入的舊變數不會自動清掉。
    </p>

    <!-- JSON 檢視／貼上 -->
    <div class="card-head sub">
      <h3 class="section-title">JSON</h3>
      <span class="spacer" />
      <UiButton size="sm" @click="jsonOpen = !jsonOpen">{{ jsonOpen ? '收合' : canEdit ? 'JSON 檢視／貼上' : 'JSON 檢視' }}</UiButton>
    </div>
    <template v-if="jsonOpen">
      <textarea
        v-if="jsonMode === 'view'"
        class="json mono"
        :value="profileJson(formJson)"
        readonly
        rows="14"
        aria-label="代管設定 JSON"
      />
      <textarea
        v-else
        v-model="jsonDraft"
        class="json mono"
        rows="14"
        spellcheck="false"
        aria-label="貼上代管設定 JSON"
      />
      <p v-if="jsonError" class="hp-issue">{{ jsonError }}</p>
      <div class="json-acts">
        <template v-if="jsonMode === 'view'">
          <UiButton size="sm" @click="copyJson">{{ copied ? '已複製 ✓' : '複製' }}</UiButton>
          <UiButton v-if="canEdit" size="sm" variant="secondary" @click="openPaste">貼上 JSON…</UiButton>
        </template>
        <template v-else>
          <UiButton size="sm" @click="cancelPaste">取消</UiButton>
          <UiButton size="sm" variant="primary" @click="applyPaste">套用到表單</UiButton>
        </template>
      </div>
      <p class="subtle">JSON 反映的是表單目前的內容（尚未儲存）；貼上的內容會先檢查格式，通過才填進表單。</p>
    </template>

    <div v-if="canEdit" class="save-bar">
      <p class="warn-text">
        ⚠ 儲存只影響<strong>之後</strong>的開站與派工；既有站台的目錄與容器不會被搬動。
        <span v-if="dirty && state !== 'none'" class="dirty">（有尚未儲存的變更）</span>
      </p>
      <div class="acts-row">
        <UiButton
          v-if="state !== 'none'"
          class="del-btn"
          :loading="deleting"
          :disabled="hostingOn"
          :title="hostingOn ? '仍開放由我方代管：請先到〈憑證設計〉關閉「開放由我方代管」' : ''"
          @click="remove"
        >
          移除代管設定…
        </UiButton>
        <UiButton @click="reset">還原</UiButton>
        <UiButton variant="primary" :loading="saving" @click="save">儲存代管設定</UiButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hp {
  display: grid;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}
.hp-head {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
}
.hp-alert,
.hp-issue {
  margin: 0;
  color: var(--danger);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.hp-alert,
.hp-issue {
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(209, 67, 67, 0.45);
  border-radius: var(--r-sm);
  background: rgba(209, 67, 67, 0.08);
}
.hp-copy {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
}
.hp-copy select {
  max-width: 260px;
}
.card-head.sub {
  margin-top: var(--sp-3);
}
.hp-grid {
  display: grid;
  gap: var(--sp-3);
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
/* 只有一格的列（出貨 channel）：寬度與下方兩格一列的「網域」對齊，不撐滿整列；窄螢幕照樣單欄。 */
.hp-grid.half {
  grid-template-columns: repeat(auto-fill, minmax(max(220px, calc((100% - var(--sp-3)) / 2)), 1fr));
}
.mono {
  font-family: var(--font-mono);
}
input.invalid,
.invalid > input,
.chk.invalid {
  border-color: var(--danger) !important;
  color: var(--danger);
}
.invalid-box,
.mem-rows.invalid,
.secret-rows.invalid {
  outline: 1px solid var(--danger);
  outline-offset: 4px;
  border-radius: var(--r-sm);
}
.mem-rows {
  display: grid;
  gap: var(--sp-2);
}
.mem-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
}
.mem-key {
  width: 200px;
}
.mem-val {
  width: 140px;
}
.feat-list {
  display: grid;
  gap: var(--sp-2);
}
/* 批151：站台機密欄位（四欄＋移除鈕；窄螢幕自然換行由 minmax 控制）。 */
.secret-rows {
  display: grid;
  gap: var(--sp-2);
}
.secret-row {
  display: grid;
  grid-template-columns:
    minmax(110px, 0.8fr) minmax(150px, 1.1fr) minmax(120px, 1fr) minmax(140px, 1.4fr)
    24px;
  gap: var(--sp-2);
  align-items: center;
}
.secret-row > input {
  min-width: 0;
}
.secret-head {
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.chk {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  font-size: var(--fs-13);
  color: var(--fg-2);
  cursor: pointer;
  user-select: none;
}
.chk input {
  margin-top: 3px;
}
.chk small {
  display: block;
  font-size: var(--fs-11);
}
.setup-field {
  margin-top: var(--sp-2);
}
.json {
  width: 100%;
  box-sizing: border-box;
  font-size: var(--fs-12);
  line-height: 1.5;
}
.json-acts {
  display: flex;
  gap: var(--sp-2);
  justify-content: flex-end;
}
/* 表格內的小圖示按鈕（與 ProductsView 的 `.x` 同一套） */
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: var(--fs-13);
  padding: 2px 4px;
  border-radius: var(--r-sm);
}
.x:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.x.danger:hover {
  color: var(--danger);
}
.save-bar {
  margin-top: var(--sp-4);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border);
}
.warn-text {
  margin: 0;
  color: var(--warning);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.dirty {
  color: var(--fg-3);
}
.acts-row {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}
.acts-row .del-btn {
  margin-right: auto;
}
/* 手機／平板（批171）：沒宣告欄的 grid 是一條 auto 欄，會被裡面不折行的內容撐開、超出卡片 → 釘住。 */
@media (--compact) {
  .hp {
    grid-template-columns: minmax(0, 1fr);
  }
}
/* 手機（批171）：欄位一律單欄；記憶體上限兩格平分整列；站台機密每列兩行
   （「欄位鍵／環境變數名／移除」一行、「名稱／說明」一行），表頭用同一套擺位。 */
@media (--mobile) {
  .hp-grid,
  .hp-grid.half {
    grid-template-columns: minmax(0, 1fr);
  }
  .mem-key,
  .mem-val {
    flex: 1 1 0;
    width: 0;
    min-width: 0;
  }
  .secret-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 24px;
    row-gap: 4px;
  }
  .secret-row > :nth-child(1) {
    grid-area: 1 / 1;
  }
  .secret-row > :nth-child(2) {
    grid-area: 1 / 2;
  }
  .secret-row > :nth-child(3) {
    grid-area: 2 / 1;
  }
  .secret-row > :nth-child(4) {
    grid-area: 2 / 2 / 3 / 4;
  }
  .secret-row > :nth-child(5) {
    grid-area: 1 / 3;
  }
  .secret-rows > .secret-row + .secret-row:not(.secret-head) {
    padding-top: var(--sp-2);
    border-top: 1px solid var(--border-2);
  }
}
</style>
